/**
 * NotificationCenter — dzwoneczek 🔔 w nagłówku.
 *
 * Pokazuje:
 *   - ✅ Zrealizowane sprawy z modułu „Sprawy restauracji" (z ostatnich 24h)
 *   - ❓ Odpowiedzi na zapytania (z ostatnich 24h)
 *
 * Starsze pozycje są dostępne w modal'u „📜 Historia powiadomień".
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const RECENT_HOURS = 24;
const RECENT_MS = RECENT_HOURS * 60 * 60 * 1000;

const fmtTimeAgo = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'przed chwilą';
    if (diffMin < 60) return `${diffMin} min temu`;
    if (diffMin < 24 * 60) return `${Math.floor(diffMin / 60)} godz temu`;
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtFull = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const isRecent = (iso) => {
  if (!iso) return false;
  try { return (Date.now() - new Date(iso).getTime()) <= RECENT_MS; }
  catch { return false; }
};

const NotificationCenter = ({
  communicatorUnread = 0,
  remindersCount = 0,
  resolutions = [],
  inquiries = [],
  lastSeenResolutionsKey = null,
  lastSeenInquiriesKey = null,
  onResolutionsSeen,
  setActiveTab,
}) => {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTab, setHistoryTab] = useState('all'); // all | resolutions | inquiries
  const wrapRef = useRef(null);

  // Tylko z ostatnich 24h trafiają do dzwoneczka
  const recentResolutions = useMemo(
    () => resolutions.filter((r) => isRecent(r.completedAt || r.updatedAt || r.createdAt)),
    [resolutions],
  );
  const recentInquiries = useMemo(
    () => inquiries.filter((q) => isRecent(q.answeredAt || q.updatedAt)),
    [inquiries],
  );

  // Liczba „nowych" — wyłącznie pośród ostatnich 24h
  const lastSeen = lastSeenResolutionsKey
    ? (() => { try { return localStorage.getItem(lastSeenResolutionsKey) || ''; } catch { return ''; } })()
    : '';
  const newResolutionsCount = recentResolutions.filter((r) => {
    const t = r.completedAt || r.updatedAt || r.createdAt;
    return t && (!lastSeen || t > lastSeen);
  }).length;

  const lastSeenInq = lastSeenInquiriesKey
    ? (() => { try { return localStorage.getItem(lastSeenInquiriesKey) || ''; } catch { return ''; } })()
    : '';
  const newInquiriesCount = recentInquiries.filter((r) => {
    const t = r.answeredAt || r.updatedAt;
    return t && (!lastSeenInq || t > lastSeenInq);
  }).length;

  const total = newResolutionsCount + newInquiriesCount;

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClick = (tab) => {
    setActiveTab(tab);
    setOpen(false);
  };

  // Po otwarciu dzwoneczka — oznacz resolutions i inquiries jako "widziane"
  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next && (newResolutionsCount > 0 || newInquiriesCount > 0) && onResolutionsSeen) {
        onResolutionsSeen();
      }
      return next;
    });
  };

  const openHistory = () => {
    setOpen(false);
    setHistoryOpen(true);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={toggleOpen}
        data-testid="notification-center-bell"
        aria-label="Centrum powiadomień"
        title="Centrum powiadomień"
        style={{
          position: 'relative',
          background: 'transparent',
          border: '1px solid var(--border)',
          color: 'var(--text)',
          width: 38,
          height: 38,
          borderRadius: 10,
          cursor: 'pointer',
          fontSize: 18,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 200ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ filter: total > 0 ? 'none' : 'grayscale(0.4)', opacity: total > 0 ? 1 : 0.7 }}>
          🔔
        </span>
        {total > 0 && (
          <span
            data-testid="notification-center-badge"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              background: '#ef4444',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px var(--bg)',
              animation: 'pulse-badge 2s ease-in-out infinite',
            }}
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="nc-dropdown"
          data-testid="notification-center-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 320,
            maxWidth: 380,
            background: 'var(--panel-bg)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.35)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <strong style={{ color: 'var(--text)', fontSize: 14 }}>🔔 Powiadomienia</strong>
            <button
              type="button"
              onClick={openHistory}
              data-testid="notification-center-open-history"
              title="Otwórz pełną historię powiadomień"
              style={{
                background: 'rgba(212,175,55,0.12)',
                color: 'var(--gold, #d4af37)',
                border: '1px solid rgba(212,175,55,0.4)',
                borderRadius: 8,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              📜 Historia
            </button>
          </div>

          <div style={{
            padding: '6px 16px',
            fontSize: 10,
            color: 'var(--text-muted)',
            background: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
          }}>
            Pokazujemy zdarzenia z ostatnich {RECENT_HOURS}h. Starsze: <strong>📜 Historia</strong>.
          </div>

          {recentResolutions.length === 0 && recentInquiries.length === 0 ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 14,
              }}
              data-testid="notification-center-empty"
            >
              <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
              <div style={{ marginBottom: 4, color: 'var(--text)', fontWeight: 600 }}>Brak nowych powiadomień</div>
              <div style={{ fontSize: 12 }}>Żadnych zdarzeń w ostatnich {RECENT_HOURS}h.</div>
            </div>
          ) : (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* === ZREALIZOWANE SPRAWY === */}
              {recentResolutions.length > 0 && (
                <>
                  <div style={{
                    padding: '10px 16px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    background: 'rgba(16, 185, 129, 0.04)',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }} data-testid="notification-center-resolutions-header">
                    <span>✅ Zrealizowane sprawy</span>
                    {newResolutionsCount > 0 && (
                      <span style={{
                        background: '#10b981', color: '#fff', fontSize: 10, padding: '2px 6px',
                        borderRadius: 999, fontWeight: 800,
                      }}>{newResolutionsCount} nowe</span>
                    )}
                  </div>
                  {recentResolutions.slice(0, 10).map((r) => {
                    const t = r.completedAt || r.updatedAt || r.createdAt;
                    const isNew = !lastSeen || (t && t > lastSeen);
                    return (
                      <button
                        key={r.id}
                        onClick={() => handleClick('restaurant_matters')}
                        data-testid={`notification-center-resolution-${r.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          width: '100%',
                          padding: '10px 16px',
                          background: isNew ? 'rgba(16,185,129,0.06)' : 'transparent',
                          color: 'var(--text)',
                          border: 'none',
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'inherit',
                          transition: 'background-color 180ms',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = isNew ? 'rgba(16,185,129,0.06)' : 'transparent'; }}
                      >
                        <span style={{
                          width: 28, height: 28, borderRadius: 8,
                          background: 'rgba(16,185,129,0.18)', color: '#10b981',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, flexShrink: 0, marginTop: 2,
                        }}>✓</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, fontWeight: 600, lineHeight: 1.3,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>{r.title || 'Bez tytułu'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                            {r.completedBy ? `${r.completedBy} zrealizował` : 'Zrealizowano'}
                            {t && <span> · {fmtTimeAgo(t)}</span>}
                          </div>
                        </div>
                        {isNew && (
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: '#10b981', flexShrink: 0, marginTop: 8,
                          }} title="Nowe" />
                        )}
                      </button>
                    );
                  })}
                </>
              )}

              {/* === ZAPYTANIA (odpowiedziane) === */}
              {recentInquiries.length > 0 && (
                <>
                  <div style={{
                    padding: '10px 16px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    background: 'rgba(59, 130, 246, 0.05)',
                    borderTop: '1px solid var(--border)',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }} data-testid="notification-center-inquiries-header">
                    <span>❓ Odpowiedzi na zapytania</span>
                    {newInquiriesCount > 0 && (
                      <span style={{
                        background: '#3b82f6', color: '#fff', fontSize: 10, padding: '2px 6px',
                        borderRadius: 999, fontWeight: 800,
                      }}>{newInquiriesCount} nowe</span>
                    )}
                  </div>
                  {recentInquiries.slice(0, 10).map((q) => {
                    const t = q.answeredAt || q.updatedAt;
                    const isNew = !lastSeenInq || (t && t > lastSeenInq);
                    return (
                      <button
                        key={q.id}
                        onClick={() => handleClick('restaurant_matters')}
                        data-testid={`notification-center-inquiry-${q.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          width: '100%',
                          padding: '10px 16px',
                          background: isNew ? 'rgba(59,130,246,0.05)' : 'transparent',
                          color: 'var(--text)',
                          border: 'none',
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'inherit',
                        }}
                      >
                        <span style={{
                          width: 28, height: 28, borderRadius: 8,
                          background: 'rgba(59,130,246,0.18)', color: '#3b82f6',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, flexShrink: 0, marginTop: 2,
                        }}>❓</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.3,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }} title={q.question}>
                            <b>Pyt:</b> {q.question}
                          </div>
                          <div style={{
                            fontSize: 13, color: 'var(--text)', marginTop: 4, fontWeight: 600, lineHeight: 1.3,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                          }} title={q.answer}>
                            💡 {q.answer}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                            {q.answeredBy} odpowiedział{q.askedByName ? ` · ${q.askedByName} pytał` : ''}
                            {t && <span> · {fmtTimeAgo(t)}</span>}
                          </div>
                        </div>
                        {isNew && (
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: '#3b82f6', flexShrink: 0, marginTop: 8,
                          }} title="Nowe" />
                        )}
                      </button>
                    );
                  })}
                </>
              )}

              <div style={{
                padding: '10px 16px',
                fontSize: 11,
                color: 'var(--text-muted)',
                textAlign: 'center',
                borderTop: '1px solid var(--border)',
              }}>
                Kliknij pozycję, aby przejść do widoku
              </div>
            </div>
          )}
        </div>
      )}

      {historyOpen && (
        <HistoryModal
          resolutions={resolutions}
          inquiries={inquiries}
          activeTab={historyTab}
          onTabChange={setHistoryTab}
          onClose={() => setHistoryOpen(false)}
          onNavigate={(tab) => { setHistoryOpen(false); setActiveTab(tab); }}
        />
      )}
    </div>
  );
};

/**
 * HistoryModal — pełna historia powiadomień (zrealizowane sprawy + zapytania).
 * Renderowany do body przez fixed overlay.
 */
const HistoryModal = ({ resolutions, inquiries, activeTab, onTabChange, onClose, onNavigate }) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const s = search.trim().toLowerCase();
  const filteredResolutions = useMemo(() => {
    const arr = [...resolutions].sort((a, b) => {
      const ta = a.completedAt || a.updatedAt || a.createdAt || '';
      const tb = b.completedAt || b.updatedAt || b.createdAt || '';
      return tb.localeCompare(ta);
    });
    if (!s) return arr;
    return arr.filter((r) =>
      `${r.title || ''} ${r.description || ''} ${r.completedBy || ''}`.toLowerCase().includes(s),
    );
  }, [resolutions, s]);

  const filteredInquiries = useMemo(() => {
    const arr = [...inquiries].sort((a, b) => {
      const ta = a.answeredAt || a.updatedAt || '';
      const tb = b.answeredAt || b.updatedAt || '';
      return tb.localeCompare(ta);
    });
    if (!s) return arr;
    return arr.filter((q) =>
      `${q.question || ''} ${q.answer || ''} ${q.askedByName || ''} ${q.answeredBy || ''}`.toLowerCase().includes(s),
    );
  }, [inquiries, s]);

  const showRes = activeTab === 'all' || activeTab === 'resolutions';
  const showInq = activeTab === 'all' || activeTab === 'inquiries';

  return createPortal(
    <div
      data-testid="notification-history-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720,
          background: 'var(--panel-bg)', border: '1px solid var(--border)',
          borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>📜 Historia powiadomień</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              Wszystkie zrealizowane sprawy i odpowiedzi na zapytania
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="notification-history-close"
            aria-label="Zamknij historię"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text)', cursor: 'pointer', fontSize: 16,
            }}
          >✕</button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '12px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <input
            type="search"
            placeholder="🔍 Szukaj w historii…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="notification-history-search"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 10,
              border: '1px solid var(--border)', background: 'var(--bg)',
              color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: `📋 Wszystko (${resolutions.length + inquiries.length})` },
              { id: 'resolutions', label: `✅ Sprawy (${resolutions.length})` },
              { id: 'inquiries', label: `❓ Zapytania (${inquiries.length})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => onTabChange(t.id)}
                data-testid={`notification-history-tab-${t.id}`}
                style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: activeTab === t.id ? 'var(--gold, #d4af37)' : 'rgba(255,255,255,0.05)',
                  color: activeTab === t.id ? '#1a1300' : 'var(--text)',
                  border: 'none',
                }}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {showRes && (
            <>
              <div style={{
                padding: '10px 20px', fontSize: 11, fontWeight: 700,
                letterSpacing: 0.5, textTransform: 'uppercase',
                color: 'var(--text-muted)', background: 'rgba(16, 185, 129, 0.04)',
                borderBottom: '1px solid var(--border)',
              }}>
                ✅ Zrealizowane sprawy ({filteredResolutions.length})
              </div>
              {filteredResolutions.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Brak pozycji
                </div>
              ) : (
                filteredResolutions.map((r) => {
                  const t = r.completedAt || r.updatedAt || r.createdAt;
                  return (
                    <button
                      key={r.id}
                      onClick={() => onNavigate('restaurant_matters')}
                      data-testid={`notification-history-resolution-${r.id}`}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
                        padding: '12px 20px', background: 'transparent',
                        color: 'var(--text)', border: 'none',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: 'rgba(16,185,129,0.18)', color: '#10b981',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, flexShrink: 0, marginTop: 2,
                      }}>✓</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{r.title || 'Bez tytułu'}</div>
                        {r.description && (
                          <div style={{
                            fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>{r.description}</div>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                          {r.completedBy ? `${r.completedBy} zrealizował` : 'Zrealizowano'}
                          {t && <span> · {fmtFull(t)}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </>
          )}

          {showInq && (
            <>
              <div style={{
                padding: '10px 20px', fontSize: 11, fontWeight: 700,
                letterSpacing: 0.5, textTransform: 'uppercase',
                color: 'var(--text-muted)', background: 'rgba(59, 130, 246, 0.05)',
                borderTop: showRes ? '1px solid var(--border)' : 'none',
                borderBottom: '1px solid var(--border)',
              }}>
                ❓ Odpowiedzi na zapytania ({filteredInquiries.length})
              </div>
              {filteredInquiries.length === 0 ? (
                <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Brak pozycji
                </div>
              ) : (
                filteredInquiries.map((q) => {
                  const t = q.answeredAt || q.updatedAt;
                  return (
                    <button
                      key={q.id}
                      onClick={() => onNavigate('restaurant_matters')}
                      data-testid={`notification-history-inquiry-${q.id}`}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
                        padding: '12px 20px', background: 'transparent',
                        color: 'var(--text)', border: 'none',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: 'rgba(59,130,246,0.18)', color: '#3b82f6',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, flexShrink: 0, marginTop: 2,
                      }}>❓</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}><b>Pyt:</b> {q.question}</div>
                        <div style={{
                          fontSize: 13, color: 'var(--text)', marginTop: 4, fontWeight: 600, lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                        }}>💡 {q.answer || <i style={{ color: 'var(--text-muted)' }}>— brak odpowiedzi —</i>}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                          {q.answeredBy ? `${q.answeredBy} odpowiedział` : 'Bez odpowiedzi'}
                          {q.askedByName && <span> · {q.askedByName} pytał</span>}
                          {t && <span> · {fmtFull(t)}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 20px', borderTop: '1px solid var(--border)',
          fontSize: 11, color: 'var(--text-muted)', textAlign: 'center',
        }}>
          Kliknij pozycję, aby przejść do modułu Sprawy restauracji
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NotificationCenter;
